import { create } from 'zustand';
import type { BoardAssetSort } from '@/lib/api/types';
import { CARD_WIDTH_DEFAULT, CARD_WIDTH_MAX, CARD_WIDTH_MIN } from '@/config/constants';
import { writeLS } from './ui';

type GridPrefs = {
  targetCardWidth: number;
  showTitles: boolean;
  sortByBoard: Record<string, BoardAssetSort>;
  view: 'grid' | 'list';
  setTargetCardWidth: (w: number) => void;
  setShowTitles: (v: boolean) => void;
  setSort: (boardId: string, sort: BoardAssetSort) => void;
  setView: (v: 'grid' | 'list') => void;
};

function load(): Pick<GridPrefs, 'targetCardWidth' | 'showTitles' | 'sortByBoard' | 'view'> {
  try {
    const raw = localStorage.getItem('gridPrefs');
    if (raw) return { targetCardWidth: CARD_WIDTH_DEFAULT, showTitles: true, sortByBoard: {}, view: 'grid', ...JSON.parse(raw) };
  } catch {
    /* ignore */
  }
  return { targetCardWidth: CARD_WIDTH_DEFAULT, showTitles: true, sortByBoard: {}, view: 'grid' };
}

export const useGridPrefs = create<GridPrefs>((set, get) => {
  const persist = () => {
    const { targetCardWidth, showTitles, sortByBoard, view } = get();
    writeLS('gridPrefs', { targetCardWidth, showTitles, sortByBoard, view });
  };
  return {
    ...load(),
    setTargetCardWidth: (w) => {
      set({ targetCardWidth: Math.min(CARD_WIDTH_MAX, Math.max(CARD_WIDTH_MIN, Math.round(w))) });
      persist();
    },
    setShowTitles: (showTitles) => {
      set({ showTitles });
      persist();
    },
    setSort: (boardId, sort) => {
      set((s) => ({ sortByBoard: { ...s.sortByBoard, [boardId]: sort } }));
      persist();
    },
    setView: (view) => {
      set({ view });
      persist();
    },
  };
});

// --- release1/A ---
/**
 * F2: per-board display fallbacks (density, group-by) used when no saved view is active, plus the device-local
 * overrides applied on top of an active view ("unsaved changes"). `gridPrefs` above keeps the global fallbacks.
 */
export type ViewDensity = 'compact' | 'comfortable' | 'spacious';
export type ViewGroupBy = 'type' | 'approval' | 'uploaded_by' | 'date_added';
export type ViewOverride = { density?: ViewDensity | null; groupBy?: ViewGroupBy | null; showTitles?: boolean };
/** target card width per density (comfortable = the grid's default) */
export const DENSITY_WIDTH: Record<ViewDensity, number> = { compact: 160, comfortable: CARD_WIDTH_DEFAULT, spacious: 360 };
export function widthToDensity(w: number): ViewDensity {
  let best: ViewDensity = 'comfortable';
  for (const [d, dw] of Object.entries(DENSITY_WIDTH) as Array<[ViewDensity, number]>) if (Math.abs(dw - w) < Math.abs(DENSITY_WIDTH[best] - w)) best = d;
  return best;
}

type BoardViewPrefs = {
  /** board id → fallback when no view is active */
  byBoard: Record<string, { density?: ViewDensity | null; groupBy?: ViewGroupBy | null }>;
  /** `${boardId}:${viewId}` → device-local overrides on top of that view */
  overrides: Record<string, ViewOverride>;
  setBoardPref: (boardId: string, patch: { density?: ViewDensity | null; groupBy?: ViewGroupBy | null }) => void;
  setOverride: (boardId: string, viewId: string, patch: ViewOverride) => void;
  clearOverride: (boardId: string, viewId: string) => void;
};
function loadViewPrefs(): Pick<BoardViewPrefs, 'byBoard' | 'overrides'> {
  try {
    const raw = localStorage.getItem('boardViewPrefs');
    if (raw) return { byBoard: {}, overrides: {}, ...JSON.parse(raw) };
  } catch {
    /* ignore */
  }
  return { byBoard: {}, overrides: {} };
}
export const useBoardViewPrefs = create<BoardViewPrefs>((set, get) => {
  const persist = () => {
    const { byBoard, overrides } = get();
    writeLS('boardViewPrefs', { byBoard, overrides });
  };
  return {
    ...loadViewPrefs(),
    setBoardPref: (boardId, patch) => {
      set((s) => ({ byBoard: { ...s.byBoard, [boardId]: { ...(s.byBoard[boardId] ?? {}), ...patch } } }));
      persist();
    },
    setOverride: (boardId, viewId, patch) => {
      set((s) => ({ overrides: { ...s.overrides, [`${boardId}:${viewId}`]: { ...(s.overrides[`${boardId}:${viewId}`] ?? {}), ...patch } } }));
      persist();
    },
    clearOverride: (boardId, viewId) => {
      set((s) => {
        const next = { ...s.overrides };
        delete next[`${boardId}:${viewId}`];
        return { overrides: next };
      });
      persist();
    },
  };
});

// --- release1/B ---
/** Release 1 S7a: sidebar + grids show archived boards/assets only while this is on (persisted per browser). */
type TidyPrefs = { showArchived: boolean; setShowArchived: (v: boolean) => void };
function loadTidy(): boolean {
  try {
    return JSON.parse(localStorage.getItem('tidyPrefs') ?? '{}').showArchived === true;
  } catch {
    return false;
  }
}
export const useTidyPrefs = create<TidyPrefs>((set) => ({
  showArchived: loadTidy(),
  setShowArchived: (showArchived) => {
    set({ showArchived });
    writeLS('tidyPrefs', { showArchived });
  },
}));
